import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    SimpleGrid,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function Recruiters() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>Graduate, volume and leadership campaigns | BetaPersona</title>
                <meta name="description" content="One workflow for the campaign. Not five publisher logins. For Head of TA and in-house recruitment teams running graduate, volume and leadership hiring." />
            </Helmet>

            <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
                <VStack spacing={12} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            One workflow for the campaign. Not five publisher logins.
                        </Heading>
                        <Text fontSize="xl" color="ink.500">
                            You run graduate campaigns, volume hiring, or leadership searches. You fire assessments: aptitude tests, personality inventories, 360 feedback. You collate results. You write packs the hiring manager can use.
                        </Text>
                        <Text fontSize="xl" color="ink.500" mt={4} mb={8}>
                            Today that means five publisher logins, email links to candidates, chasing completions, downloading PDFs, and copy-pasting scale scores into Word. BetaPersona is one workflow: fire, store, generate. One login. No portal-hopping.
                        </Text>
                        <Image
                            src="/images/ta-5439478.jpeg"
                            alt="Professionals discussing data and analysis. Photo: Tima Miroshnichenko, Pexels"
                            borderRadius="md"
                            w="full"
                            h="auto"
                            objectFit="cover"
                            aspectRatio="16/9"
                        />
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                        <Box bg="navy.500" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Graduate campaigns
                            </Heading>
                            <Text>
                                Annual intake of 50, 100, or 500 graduates. Standard battery: aptitude (numerical, verbal, abstract reasoning) plus personality inventory. You fire 500 assessments, track completions, shortlist top candidates, run a second-stage battery (deeper personality, simulation, group assessment). Hiring managers receive packs for final interviews.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                Volume + speed + standardization = one workflow, or chaos.
                            </Text>
                        </Box>

                        <Box bg="navy.600" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Volume hiring
                            </Heading>
                            <Text>
                                Call centre, retail, logistics, nursing. High volume, tight timelines. Aptitude and personality assessments at scale. You need fast turnaround: candidate completes assessment today, hiring manager sees the pack tomorrow. No time to log into three publisher portals and collate by hand.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                Speed matters. One system. One workflow.
                            </Text>
                        </Box>

                        <Box bg="navy.700" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Leadership campaigns
                            </Heading>
                            <Text>
                                Executive search. Senior hires. Bespoke batteries: Hogan HPI + HDS + MVPI, plus 360 feedback, plus simulation or assessment centre. Multiple stakeholders. Confidential. The hiring manager (or the board) receives a comprehensive pack: psychometric results, 360 data, and competency framework mapping.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                Bespoke + confidential + comprehensive = managed access + derived packs.
                            </Text>
                        </Box>
                    </SimpleGrid>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What goes wrong today
                        </Heading>
                        <VStack align="start" spacing={6}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Five publisher logins
                                </Heading>
                                <Text>
                                    Aptitude tests from one publisher. Personality from another. 360 from a third. Simulation from a fourth. If you add a bespoke instrument, that's a fifth login. Graduate campaign with 500 candidates = 500 assessments × 5 logins = 2,500 interactions. That is the opposite of a workflow.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Email links and chasing completions
                                </Heading>
                                <Text>
                                    Fire the assessment from publisher portal. Publisher emails the candidate. Candidate clicks link (or does not click link). You log back into publisher portal to check if they completed. Multiply by 500 candidates × 3 assessments. Now you are chasing 1,500 email links.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Downloading PDFs and collating by hand
                                </Heading>
                                <Text>
                                    Candidate completes three assessments. You log into three publisher portals. You download three PDFs. You open three PDFs. You pull scale scores from PDF one, paste into Word. You pull scale scores from PDF two, paste into Word. You pull scale scores from PDF three, paste into Word. You map to competency framework. You write the narrative. You send the pack to the hiring manager.
                                </Text>
                                <Text mt={3} fontWeight="600">
                                    Multiply by 50 shortlisted candidates. You have just collated 150 PDFs by hand.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Hiring managers receive raw reports
                                </Heading>
                                <Text>
                                    Under time pressure, you forward the publisher PDF to the hiring manager. The hiring manager does not have a psychometric or test-user qualification. They cannot interpret scale scores and percentile ranks. They email you for explanations. You have now created more work, not less.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Files scattered across laptops and email
                                </Heading>
                                <Text>
                                    Graduate joins. Three years later, they are up for promotion. Where is the original assessment from recruitment? Laptop folder? Email attachment? Publisher portal (but the 90-day retention expired)? SharePoint (but which folder)?
                                </Text>
                                <Text mt={3}>
                                    You own the person after they join. The agency's contract ended. The publisher's 90-day window expired. You need a repository with managed access. You do not have one.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What BetaPersona provides
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    One workflow for the campaign
                                </Heading>
                                <Text>
                                    Fire all assessments from one login. Live integration with your publishers of choice e.g. Psytech. Results land in the repository automatically. Track completions from one dashboard. No logging into five publisher portals. No chasing 1,500 email links.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    One repository with managed access
                                </Heading>
                                <Text>
                                    Aptitude, personality, 360, performance data—one store. You decide who can see what. Talent team sees publisher reports. Hiring managers see derived packs. Audit log records who viewed what, when. GDPR Article 9: special-category data, managed properly.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Business-ready packs at scale
                                </Heading>
                                <Text>
                                    Generate packs derived from publisher reports and mapped to your competency framework. Graduate campaign with 50 shortlisted candidates = 50 packs, generated in one sitting. No copy-pasting scale scores from 150 PDFs into Word. The hiring manager receives a pack they can use without a psychometric or test-user qualification.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    White-labelled client portal
                                </Heading>
                                <Text>
                                    The portal carries your organization's brand, not BetaPersona. Hiring managers log into a system that looks like your HRIS, not a third-party assessment platform. For assessment practices: white-labelled portals for each client organization.
                                </Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Who this is for
                        </Heading>
                        <Text mb={4}>
                            <strong>Head of Talent Acquisition.</strong> Head of in-house Recruitment. Talent Acquisition Manager. In-house recruitment teams running graduate campaigns, volume hiring, or leadership searches.
                        </Text>
                        <Text mb={4}>
                            <strong>Not for high-street recruiters or CV-only agencies.</strong> If you place candidates and hand them over at contract signature, BetaPersona is not your system. This is for employers who own the person after shortlist and need a repository for recruitment, promotion, development, and succession planning.
                        </Text>
                        <Text>
                            <strong>Not an ATS.</strong> BetaPersona is not an applicant tracking system. We integrate with your ATS. We do not replace it. We store assessment results, apply access policy, and generate derived packs. The ATS manages candidates, job postings, and workflow. BetaPersona manages assessments.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            One workflow for the campaign
                        </Heading>
                        <Text mb={4}>
                            Fire all assessments from one login. Live integration with your publishers of choice e.g. Psytech.
                        </Text>
                        <Text mb={4}>
                            Example of integration with a Psytech account: you add the candidate to BetaPersona, link them to a desired position or competency report or battery. BetaPersona provisions that candidate on the publisher portals. The candidate logs in to your branded BetaPersona and follows links to the publisher to perform assessments.
                        </Text>
                        <Text mb={4}>
                            All publishers (Psytech, Hogan, SHL, Thomas etc) are integrated based on your account/relationship with them.
                        </Text>
                        <Text>
                            This is not a self-serve assessment shop.
                        </Text>
                    </Box>

                    <Box bg="navy.500" color="white" p={10} borderRadius="md" textAlign="center">
                        <Heading as="h2" size="xl" mb={6}>
                            Talk to us
                        </Heading>
                        <Text fontSize="lg" mb={8}>
                            We will walk you through the workflow for graduate, volume and leadership campaigns.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
